import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CInputGroup,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilBell,
  cilCheckCircle,
  cilClock,
  cilPlus,
  cilReload,
  cilSearch,
  cilWarning,
} from '@coreui/icons'
import Table from './Table'
import AddForm from './AddForm'
import EditForm from './EditForm'
import TeamAlertService from 'src/service/TeamAlertService'
import './TeamAlert.css'

const TeamAlert = () => {
  const [users, setUsers] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [titleFilter, setTitleFilter] = useState('')
  const [addVisible, setAddVisible] = useState(false)
  const [editAlertId, setEditAlertId] = useState(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    if (titleFilter) params.append('title', titleFilter)
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    try {
      const result = await TeamAlertService.getTeamAlert(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnSorter, titleFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Debounce the search box, same as the other refreshed listings.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if (titleFilter === term) return
      setActivePage(1)
      setTitleFilter(term)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, titleFilter])

  const { sentCount, pendingCount } = useMemo(() => {
    const alerts = users?.data || []
    return {
      sentCount: alerts.filter((a) => a.is_sent === 1).length,
      pendingCount: alerts.filter((a) => a.is_sent !== 1).length,
    }
  }, [users])

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title">Team Alerts</h1>
          <p className="ge-page-subtitle">
            Write an alert, then send it as a push notification to every active member.
          </p>
        </div>
        <CButton color="primary" onClick={() => setAddVisible(true)}>
          <CIcon icon={cilPlus} className="me-1" />
          Add Alert
        </CButton>
      </div>

      {error && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load your team alerts.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchUsers}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="ta-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="ta-stat-icon ta-stat-icon--primary">
                <CIcon icon={cilBell} size="lg" />
              </span>
              <div>
                <div className="ta-stat-label">Total Alerts</div>
                <div className="ta-stat-value">{users?.total ?? 0}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="ta-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="ta-stat-icon ta-stat-icon--success">
                <CIcon icon={cilCheckCircle} size="lg" />
              </span>
              <div>
                <div className="ta-stat-label">Sent on This Page</div>
                <div className="ta-stat-value">{sentCount}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="ta-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="ta-stat-icon ta-stat-icon--warning">
                <CIcon icon={cilClock} size="lg" />
              </span>
              <div>
                <div className="ta-stat-label">Pending on This Page</div>
                <div className="ta-stat-value">{pendingCount}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <CInputGroup className="ge-search">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title..."
                aria-label="Search alerts by title"
              />
            </CInputGroup>
          </div>

          <Table
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnSorter={columnSorter}
            loading={loading}
            onEditAlert={setEditAlertId}
          />
        </CCardBody>
      </CCard>

      <AddForm visible={addVisible} onClose={() => setAddVisible(false)} fetchUsers={fetchUsers} />

      <EditForm
        alertId={editAlertId}
        visible={!!editAlertId}
        onClose={() => setEditAlertId(null)}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default TeamAlert
